import { __callbacks } from "../Callbacks/Callback";
import { CallbackManager } from "../Callbacks/CallbackManager";
import { __debug } from "../Debug/Debug";
import { __delay } from "../Delay/Delay";
import { Chat } from "../Enums";
import {
    IChat,
    IChatMessageFragment,
    IChatMessage,
    IChatsChatState
} from "../Interfaces";
import { ChatMessage } from "./ChatMessage";
import { ChatMessageFragment } from "./ChatMessageFragment";
import { __sb } from "../SB/sb";
import { __pusher } from "../Pusher/pusher"; // Import Pusher
import { __utils } from "../Utils";

class Chats {
    private keyLocalStorage: string;
    private canSaveToLocalStorage: boolean = true;
    private isLocal: boolean = false;
    private chats: IChat = {};
    private chatStates: IChatsChatState = {};
    private totalChats: number = 0;
    private currentChatId: string = "";

    private initFromLocalStorage = new CallbackManager("initFromLocalStorage", true);
    public totalConversations = new CallbackManager("totalConversations", false);

    constructor() {
        this.keyLocalStorage = "chats";
        this.initEvents();
        this.loadFromLocalStorage();
    }

    public onInitFromLocalStorage = (callback: Function) => {
        if (this.isLocal) {
            callback(this.get());
            return;
        }
        this.initFromLocalStorage.register(callback);
    }

    public onTotalConversations = (callback: Function) => {
        callback({ totalConversations: this.totalChats });
        return this.totalConversations.register(callback);
    }

    public createNewChat = (chatMessage: ChatMessage) => {
        const chatId = chatMessage.chatId;
        const messageNo = chatMessage.messageNo;

        if (!chatId) {
            __debug.warn(() => {
                console.warn(`Invalid chat ID: ${chatId}`);
            });
            return;
        }

        if (!this.isChat(chatId)) {
            this.chats[chatId] = [];
            this.chatStates[chatId] = {
                chatId: chatId,
                disableInputs: false,
                respondedUserOptions: [],
                unreadMsgs: 0
            };
            this.handleOnNewChat();
        }

        if (this.isMessage(chatId, messageNo)) {
            __debug.warn(() => {
                console.warn(`Message No: [${messageNo}] overwritten on the chat Id: [${chatId}]`);
            });
        }

        this.chats[chatId].push(chatMessage);
        this.saveToLocalStorage();
    }

    public addMessage = (chatMessage: ChatMessage) => {

        const chatId = chatMessage.chatId;

        this.createNewChat(chatMessage);

        // Translate links in message fragments
        chatMessage.fragments = chatMessage.fragments.map((fragment) => {
            if (fragment.props.text) {
                const originalText = fragment.props.text;
                const updatedText = __utils.detectAndTranslateLinks(fragment.props.text);

                return new ChatMessageFragment({
                    ...fragment,
                    props: {
                        ...fragment.props,
                        text: updatedText
                    }
                });
            }
            return fragment;
        });

        const messageText = chatMessage.fragments.length > 0 ? chatMessage.fragments[0].props.text : undefined;

        // Prevent re-sending bot messages
        if (chatMessage.messageType === "MessageSend" && !chatMessage.props.isBotResponse && localStorage.getItem('bot_conversation' + chatMessage.chatId) !== 'true') {
            if (messageText) {
                __sb.sendMessage(chatMessage.chatId, chatMessage, messageText.toString(), false);
            }
        }

        // Trigger callback for updating the conversation window
        __callbacks.chatMessage.trigger({
            chatId: chatMessage.chatId,
            lastFragment: chatMessage.fragments[chatMessage.fragments.length - 1],
        });
    }

    public addFragment = (chatMessageFragment: ChatMessageFragment) => {
        const { chatId, messageNo } = chatMessageFragment;

        if (!chatId) {
            __debug.warn(() => {
                console.warn(`Invalid chat ID: ${chatId}`);
            });
            return;
        }

        if (!this.isChat(chatId)) {
            __debug.warn(() => {
                console.warn(`Chat with the Id: [${chatId}] does not exist!`);
            });
            return;
        }

        if (!this.isMessage(chatId, messageNo)) {
            __debug.warn(() => {
                console.warn(`Message No: [${messageNo}] does not exist on the chat id: [${chatId}]!`);
            });
            return;
        }

        const message = this.chats[chatId].find(msg => msg.messageNo === messageNo);
        if (message) {
            message.fragments.push(chatMessageFragment);
        }

        const messageText = chatMessageFragment.props.text;
        if (message?.messageType === "MessageSend" && !message.props.isBotResponse) {
            const contact = this.getContactId();
            if (contact && messageText) {
                __sb.sendMessageFragment(chatId, chatMessageFragment, messageText.toString());
            }
        }

        this.saveToLocalStorage();
    }

    public getLastFragment = (chatId: string, messageNo: number): IChatMessageFragment | undefined => {
        if (!this.isChat(chatId)) return undefined;
        if (!this.isMessage(chatId, messageNo)) return undefined;
        const message = this.chats[chatId].find(msg => msg.messageNo === messageNo);
        return message?.fragments[message.fragments.length - 1] ;
    }

    public getFirstFragmentOfLastMessage = (chatId: string): IChatMessageFragment | undefined => {
        if (!this.isChat(chatId)) return;
        const message = this.chats[chatId][0];
        return message?.fragments[0];
    }

    public getChatStates(): IChatsChatState {
        return this.chatStates;
    }

    public get = (): IChat => {
        return this.chats;
    }

    public isChat = (chatId: string): boolean => {
        return !!this.chats[chatId];
    }

    public isMessage = (chatId: string, messageNo: number): boolean => {
        return !!this.chats[chatId]?.find(msg => msg.messageNo === messageNo);
    }

    private saveToLocalStorage = () => {
        if (!this.canSaveToLocalStorage) return;

        __delay.set("chats", () => {
            localStorage.setItem(this.keyLocalStorage, JSON.stringify(this.chats));
            __debug.warn(() => {
                console.warn("Saved to local storage!");
            });
        }, 1000);
    }

    private loadFromLocalStorage = () => {
        const chatsStr = localStorage.getItem(this.keyLocalStorage);
        if (!chatsStr) {
            //console.log("No chats found in local storage.");
            return;
        }

        let chats: IChat = {};
        try {
            chats = JSON.parse(chatsStr);
        } catch (error) {
            __debug.warn(() => {
                console.warn("Invalid chats JSON from local storage!");
            });
            return;
        }

        this.canSaveToLocalStorage = false;

        Object.keys(chats).forEach(chatId => {
            if (!chatId) {
                __debug.warn(() => {
                    console.warn(`Invalid chat ID in local storage: ${chatId}`);
                });
                return;
            }

            const chatMessages = chats[chatId];
            const filteredMessages: IChatMessage[] = chatMessages.map(msg => new ChatMessage(msg));

            this.chats[chatId] = filteredMessages;

            // Initialize Pusher for all chats
            __pusher.createChannel(chatId);
        });

        this.totalChats = Object.keys(this.chats).length;
        this.totalConversations.trigger({ totalConversations: this.totalChats });

        this.canSaveToLocalStorage = true;
        this.initFromLocalStorage.trigger({ ...this.get() });
    }

    public getLastMessageNo = (chatId: string): number | undefined => {
        if (!this.isChat(chatId)) return;
        const messageNos = this.chats[chatId].map(msg => msg.messageNo).sort((a, b) => b - a);
        return messageNos.length > 0 ? messageNos[0] : undefined;
    }

    public getLastFragmentOfLastMessage = (chatId: string): IChatMessageFragment | undefined => {
        if (!this.isChat(chatId)) return undefined;

        const messageNo = this.getLastMessageNo(chatId);
        if (messageNo === undefined) return undefined;

        if (!this.isMessage(chatId, messageNo)) return undefined;

        const lastFragment = this.getLastFragment(chatId, messageNo);

        return lastFragment;
    }

    private handleOnNewChat = () => {
        this.totalChats++;
        this.totalConversations.trigger({ totalConversations: this.totalChats });
    }

    public destroy = () => {
        this.chats = {};
    }

    private handleChatMessage = (_data: any) => {
        const { type, data } = _data;

        if (!(data instanceof ChatMessage) && !(data instanceof ChatMessageFragment)) {
            console.error("Invalid data type passed to handleChatMessage:", data);
            return;
        }

        switch (type) {
            case Chat.Message:
                if (data instanceof ChatMessage) {
                    this.addMessage(data);
                } else {
                    console.error("Expected ChatMessage but received ChatMessageFragment");
                }
                break;
            case Chat.Fragment:
                if (data instanceof ChatMessageFragment) {
                    this.addFragment(data);
                } else {
                    console.error("Expected ChatMessageFragment but received ChatMessage");
                }
                break;
            default:
                break;
        }

        __delay.set("handleChatMessage", () => {
            const chatId = data.chatId;
            __callbacks.chatMessage.trigger({
                chatId: chatId,
                lastFragment: this.getLastFragmentOfLastMessage(chatId),
            });
        }, 1000);
    }

    private initEvents = () => {
        this.onInitFromLocalStorage(() => {
            this.isLocal = true;
        });

        __callbacks.onMessageSend(this.handleChatMessage);
        __callbacks.onMessageReceive(this.handleChatMessage);
        __callbacks.onMessageNotify(this.handleChatMessage);
        __callbacks.onFormUpdate(this.handleChatMessage);
        __callbacks.onMsgUpdate(this.handleChatMessage);
        __callbacks.onFileUploadUpdate((data: any) => {
            setTimeout(() => {
                this.handleChatMessage(data);
            }, 500);
        });
    }

    public getIds = (): string[] => {
        return Object.keys(this.chats);
    }

    public setCurrentChatId = (chatId: string) => {
        this.currentChatId = chatId;
        __callbacks.chatId.trigger(this.currentChatId);
    }

    public getCurrentChatId = (): string => {
        return this.currentChatId;
    }

    private getUid(): string {
        return window.location.search.substr(1).split("&")[0].split("=")[1];
    }

    public getContactId(): string | null {
        return localStorage.getItem('contact_id' + this.getUid());
    }
}

export const __chats = new Chats();
